import { useMemo, useRef, useState } from "react";
import { submitTurn } from "../api.js";
import { readError } from "../errors.js";
import Icon from "../ui/Icon.jsx";

// 서버(functions/src/config.js)와 같은 값. 다르면 서버가 거절하고 턴만 날린다.
const MIN_INPUT_CHARS = 80;
const USER_SEQS = [1, 3, 5];

const GUIDE = {
  1: { label: "입론 쓰기", hint: "왜 그렇게 생각하는지, 근거까지 같이 써주세요." },
  3: { label: "답하고 반박하기", hint: "질문에 답하고, 동의할 수 없는 지점 하나를 고르세요." },
  5: { label: "최종변론", hint: "바뀌었다면 바뀐 대로 쓰면 돼요." },
};

export default function Debate({ session, onTurns, onDone, onLeave }) {
  const { debate, turns } = session;
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);
  const box = useRef(null);

  // 다음에 내가 말할 차례. 서버가 진실이지만, 화면은 받은 턴으로 계산한다.
  const seq = useMemo(
    () => USER_SEQS.find((s) => !turns.some((t) => t.seq === s)) ?? null,
    [turns],
  );

  const short = MIN_INPUT_CHARS - text.trim().length;
  const ready = short <= 0 && !sending;
  const spoken = turns.filter((t) => t.speaker === "user").length;

  async function send() {
    if (!ready || seq == null) return;
    const content = text;
    setSending(true);
    setErr(null);
    try {
      const { aiSeq, turn } = await submitTurn(debate.id, seq, content);

      // 사용자 턴은 서버가 돌려주지 않는다. 보낸 내용을 그대로 붙인다.
      const next = [...turns, { seq, speaker: "user", content }];
      if (aiSeq && turn) next.push({ ...turn, seq: aiSeq, speaker: "ai" });
      onTurns(next);

      setText("");                      // 성공했을 때만 비운다
      if (aiSeq === null) onDone();     // T5 를 보냈으면 확신도 화면으로
    } catch (e) {
      // 실패해도 text 는 그대로 둔다. 검증에서 이탈 원인 1순위였다.
      setErr(e);
    } finally {
      setSending(false);
    }
  }

  const lastAi = [...turns].reverse().find((t) => t.speaker === "ai");
  const guide = GUIDE[seq] ?? GUIDE[5];

  return (
    <main className="screen" style={{ minHeight: "100vh" }}>
      <header className="bar">
        <button type="button" className="icon-btn" aria-label="나가기" onClick={onLeave}>
          <Icon name="back" />
        </button>
        <span className="d" style={{
          flexGrow: 1, minWidth: 0, fontSize: 21,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {debate.topicSnapshot?.title}
        </span>
        <span className="pill mark">{Math.min(turns.length + 1, 6)}/6</span>
      </header>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <div className="dots" aria-label={`내 발언 ${spoken}/3`}>
          {[1, 2, 3, 4, 5, 6].map((n) => {
            const t = turns.find((x) => x.seq === n);
            const cls = t ? (t.speaker === "ai" ? "dot ai" : "dot done") : n === seq ? "dot now" : "dot todo";
            return <span key={n} className={cls} />;
          })}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <span className="pill mark" style={{ fontSize: 12 }}>나 · {debate.stanceLabel}</span>
          <span className="pill alert" style={{ fontSize: 12 }}>AI · 반대편</span>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }} ref={box}>
        {turns.map((t) =>
          t.speaker === "user" ? (
            <div key={t.seq} className="mine">
              <span style={{ fontSize: 12, fontWeight: 800, color: "var(--red)" }}>내 발언</span>
              <span style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.55 }}>{t.content}</span>
            </div>
          ) : (
            <article key={t.seq} className="theirs">
              <span className="pill alert" style={{ alignSelf: "flex-start", fontSize: 12 }}>AI · 반대편</span>
              <p className="speech">{t.content}</p>
              {t.meta?.question && (
                <div className="ask">
                  <span className="label">질문</span>
                  <p>{t.meta.question}</p>
                </div>
              )}
            </article>
          ),
        )}

        {sending && (
          <article className="theirs" aria-live="polite" aria-busy="true">
            <span className="pill alert" style={{ alignSelf: "flex-start", fontSize: 12 }}>AI · 반대편</span>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span className="think" style={{ display: "flex", gap: 5 }}><span /><span /><span /></span>
              <span style={{ fontSize: 15, fontWeight: 800 }}>생각하는 중…</span>
            </div>
            <div className="skeleton" />
            <div className="skeleton" style={{ width: "62%" }} />
          </article>
        )}

        {turns.length === 0 && !sending && (
          <article className="card ai raised">
            <span className="pill" style={{ alignSelf: "flex-start", fontSize: 12 }}>첫 발언</span>
            <h2 className="d" style={{ fontSize: 26, lineHeight: 1.2 }}>먼저 내 생각을 써요</h2>
            <p style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.6 }}>
              왜 그렇게 생각하는지, 근거까지 같이 써주세요. 이걸 읽고 AI가 반대편에 섭니다.
            </p>
          </article>
        )}
      </div>

      {err && (
        <section role="alert" className="card alert raised">
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <Icon name="warn" size={22} width={2.8} />
            <h2 className="d" style={{ fontSize: 22, lineHeight: 1.2 }}>{readError(err).title}</h2>
          </div>
          <p style={{ fontSize: 14, fontWeight: 800, lineHeight: 1.55 }}>
            쓰신 내용은 아래에 그대로 있어요. 아무것도 사라지지 않았어요.
          </p>
          {readError(err).hint && (
            <p style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.5 }}>{readError(err).hint}</p>
          )}
        </section>
      )}

      <span className="grow" />

      <form className="composer" onSubmit={(e) => { e.preventDefault(); send(); }}>
        <label htmlFor="reply" className="label">내 차례 · {guide.label}</label>
        <textarea
          id="reply"
          value={text}
          placeholder={guide.hint}
          onChange={(e) => setText(e.target.value)}
          disabled={sending}
        />
        <div className="row">
          <span className="pill" style={{ background: short > 0 ? "var(--red)" : "var(--white)" }}>
            {short > 0 ? `${short}자 더 필요해요` : `${text.trim().length}자`}
          </span>
          <button type="submit" className="btn primary" disabled={!ready} style={{ minHeight: 46 }}>
            {sending ? "보내는 중…" : err ? "다시 보내기" : "보내기"}
          </button>
        </div>
      </form>

      {lastAi && <p className="note">매 턴 저장돼요. 창을 닫아도 여기서부터 이어서 할 수 있어요.</p>}
    </main>
  );
}
